import React, { lazy, Suspense } from 'react';
import { Layout } from '@/components/layout/Layout';
import { PromoBannerRow } from '@/components/home/PromoBannerRow';
import { TrustBadges } from '@/components/home/TrustBadges';
import { useJudgeMeReviews } from '@/hooks/useJudgeMeReviews';
import { SEOHead } from '@/components/SEOHead';

const ShopByCategory = lazy(() => import('@/components/home/ShopByCategory').then(m => ({ default: m.ShopByCategory })));
const ShopifyFeaturedProducts = lazy(() => import('@/components/home/ShopifyFeaturedProducts').then(m => ({ default: m.ShopifyFeaturedProducts })));
const WhyChooseUs = lazy(() => import('@/components/home/WhyChooseUs').then(m => ({ default: m.WhyChooseUs })));
const SaleCountdown = lazy(() => import('@/components/home/SaleCountdown').then(m => ({ default: m.SaleCountdown })));
const BlogPreview = lazy(() => import('@/components/home/BlogPreview').then(m => ({ default: m.BlogPreview })));
const CustomerReviews = lazy(() => import('@/components/home/CustomerReviews').then(m => ({ default: m.CustomerReviews })));
const HomeFAQ = lazy(() => import('@/components/home/HomeFAQ').then(m => ({ default: m.HomeFAQ })));

import { faqSchema } from '@/components/home/HomeFAQ';

const SITE_URL = "https://giztoo.com";

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "GizToo",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description: "Online store for gadgets, electronics, and accessories at great prices.",
  contactPoint: {
    "@type": "ContactPoint",
    email: "info@giztoo.com",
    contactType: "customer service",
  },
  sameAs: [
    "https://facebook.com/giztoo",
    "https://instagram.com/giztoo",
    "https://twitter.com/giztoo",
  ],
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "GizToo",
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/deals?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

const Index = () => {
  const { data: reviews } = useJudgeMeReviews();

  const reviewCount = reviews?.length || 0;
  const avgRating = reviewCount > 0
    ? +(reviews!.reduce((sum, r) => sum + r.rating, 0) / reviewCount).toFixed(1)
    : undefined;

  const aggregateRatingSchema = avgRating ? {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "GizToo",
    url: SITE_URL,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avgRating,
      reviewCount,
      bestRating: 5,
      worstRating: 1,
    },
  } : null;

  return (
    <Layout>
      <SEOHead
        title="GizToo — Tech Gadgets & Accessories | Free Shipping $35+"
        description="Shop the latest tech gadgets, phone accessories, smart home devices, and more at GizToo. Free shipping on orders $35+. 30-day returns."
        canonicalPath="/"
      />
      <h1 className="sr-only">GizToo — Premium Tech Gadgets & Accessories</h1>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <PromoBannerRow />
      <TrustBadges />
      <Suspense fallback={null}>
        <ShopByCategory />
        <ShopifyFeaturedProducts />
        <WhyChooseUs />
        <SaleCountdown />
        <CustomerReviews />
        {aggregateRatingSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(aggregateRatingSchema) }}
          />
        )}
        <BlogPreview />
        <HomeFAQ />
      </Suspense>
    </Layout>
  );
};

export default Index;
